import { useCartState, type OrderItems } from "~/stores/storeCart";
import { onMounted, onUnmounted } from "vue";
import { useAuthHandler } from "~/composables/useAuthHandler";
import { determineSyncAction, type SyncResult } from "~/utils/timestamp";

export function useCartSync() {
  const cartState = useCartState();
  const { isProcessingSignOut } = useAuthHandler();
  let intervalId: NodeJS.Timeout | null = null;

  function clearIntervalWhenCartEmpty(state: typeof cartState) {
    if (state.orderItems.length === 0) {
      state.orderInterval = "";
    } else if(!(state.orderInterval === "" || state.orderInterval === "month" || state.orderInterval === "hour")){
      state.orderInterval = "";
      state.orderItems = [];
    }
  }

  // 深度比較兩個對象是否相等
  function deepEqual(obj1: any, obj2: any): boolean {
    if (obj1 === obj2) return true;
    
    if (obj1 == null || obj2 == null) return obj1 === obj2;
    
    if (typeof obj1 !== typeof obj2) return false;
    
    if (typeof obj1 !== 'object') return obj1 === obj2;
    
    if (Array.isArray(obj1) !== Array.isArray(obj2)) return false;
    
    if (Array.isArray(obj1)) {
      if (obj1.length !== obj2.length) return false;
      for (let i = 0; i < obj1.length; i++) {
        if (!deepEqual(obj1[i], obj2[i])) return false;
      }
      return true;
    }
    
    const keys1 = Object.keys(obj1);
    const keys2 = Object.keys(obj2);
    
    if (keys1.length !== keys2.length) return false;
    
    for (const key of keys1) {
      if (!keys2.includes(key)) return false;
      if (!deepEqual(obj1[key], obj2[key])) return false;
    }
    
    return true;
  }

  async function useCartRetrieve(cartStore: any) {
    // 如果正在登出中或正在同步中，跳過獲取
    if (isProcessingSignOut.value || cartStore.isSyncing) {
      console.log("Sign out in progress or syncing in progress, skipping cart retrieve");
      return;
    }
    
    try {
      const res = await $fetch<any>('/api/proxy/api/v3/gsn/cart/items', {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
        },
      });
      
      const newOrderItems = res.content.cart ?? [];
      const newOrderInterval = res.content.interval ?? "";
      const serverTimestamp = res.content.lastModified;
      
      // Determine sync action based on timestamps
      const syncResult: SyncResult = determineSyncAction(
        cartStore.lastModified,
        serverTimestamp
      );
      
      // Log sync result in development mode
      if (process.env.NODE_ENV === 'development') {
        console.log('Cart sync result:', {
          updated: syncResult.updated,
          reason: syncResult.reason,
          clientTime: syncResult.clientTimestamp,
          serverTime: syncResult.serverTimestamp
        });
      }
      
      // Handle sync based on result
      if (syncResult.reason === 'no_server_timestamp') {
        // Use existing deepEqual logic for backward compatibility
        const itemsChanged = !deepEqual(cartStore.orderItems, newOrderItems);
        const intervalChanged = cartStore.orderInterval !== newOrderInterval;
        
        if (itemsChanged || intervalChanged) {
          cartStore.orderItems = newOrderItems;
          cartStore.orderInterval = newOrderInterval;
          clearIntervalWhenCartEmpty(cartStore);
        }
      } else if (syncResult.updated) {
        // Server data is newer, update client
        cartStore.orderItems = newOrderItems;
        cartStore.orderInterval = newOrderInterval;
        cartStore.lastModified = serverTimestamp;
        clearIntervalWhenCartEmpty(cartStore);
      }
      // If client is newer or timestamps are equal, don't update
      
    } catch (error: any) {
      console.error("Error in useCartRetrieve:", error);
      // 401 錯誤由 api-interceptor 統一處理，這裡不再重複處理
    }
  }

  function startCartSync() {
    intervalId = setInterval(() => {
      // 如果正在登出中或正在同步中，跳過這次同步
      if (isProcessingSignOut.value || cartState.isSyncing) {
        return;
      }
      if (document.visibilityState === "visible" && document.hasFocus()) {
        useCartRetrieve(cartState);
      }
    }, 30000);
  }

  function stopCartSync() {
    if (intervalId) {
      clearInterval(intervalId);
      intervalId = null;
    }
  }

  onMounted(() => {
    // 初始載入時抓取一次
    useCartRetrieve(cartState);
    // 啟動定時同步
    startCartSync();
  });

  onUnmounted(() => {
    stopCartSync();
  });

  return { useCartRetrieve };
}